import React from 'react';
import { useIntl } from 'react-intl';
import type { UserProfileData } from '@contexts/AuthContextStore';
import IntentCard from '@components/2-molecules/IntentCard';
import {
  FieldStack,
  InlineError,
  StepSubtitle,
  StepTitle,
  TextInput,
} from '../Onboarding.styled';
import { INTENT_OPTIONS } from '../options';
import { Grid } from './StepIntent.styled';

interface StepIntentProps {
  data: UserProfileData;
  errors: Partial<Record<keyof UserProfileData, string>>;
  update: (patch: Partial<UserProfileData>) => void;
}

const StepIntent: React.FC<StepIntentProps> = ({ data, errors, update }) => {
  const intl = useIntl();
  const intents = data.intents ?? [];
  const showOther = intents.includes('other');

  const toggle = (id: string) => {
    const next = new Set(intents);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    const nextArr = Array.from(next);
    const patch: Partial<UserProfileData> = { intents: nextArr };
    if (!nextArr.includes('other')) {
      patch.intentsOther = '';
    }
    update(patch);
  };

  return (
    <div data-testid="onboarding-step-intent">
      <StepTitle>
        {intl.formatMessage({ id: 'onboarding.intent.title' })}
      </StepTitle>
      <StepSubtitle>
        {intl.formatMessage({ id: 'onboarding.intent.subtitle' })}
      </StepSubtitle>

      <FieldStack>
        <Grid
          role="group"
          aria-label={intl.formatMessage({ id: 'onboarding.intent.options' })}
        >
          {INTENT_OPTIONS.map((opt) => (
            <IntentCard
              key={opt.id}
              id={opt.id}
              title={opt.label}
              description={opt.description}
              selected={intents.includes(opt.id)}
              onToggle={toggle}
            />
          ))}
        </Grid>

        {errors.intents && (
          <InlineError data-testid="onboarding-step-intent__error" role="alert">
            {errors.intents}
          </InlineError>
        )}

        {showOther && (
          <>
            <TextInput
              data-testid="onboarding-input-intentsOther"
              value={data.intentsOther ?? ''}
              onChange={(e) => update({ intentsOther: e.target.value })}
              placeholder={intl.formatMessage({
                id: 'onboarding.intent.other.placeholder',
              })}
            />
            {errors.intentsOther && (
              <InlineError
                data-testid="onboarding-input-intentsOther__error"
                role="alert"
              >
                {errors.intentsOther}
              </InlineError>
            )}
          </>
        )}
      </FieldStack>
    </div>
  );
};

export default StepIntent;
